import React, { useState, useEffect } from "react";
import { Card, CardHeader, CardContent, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { toast } from "@/components/ui/use-toast";
import { useAuth } from "@/hooks/useAuth";
import ApiService from "@/services/api";
import { EndpointKeys } from "@/config/api";
import MenuBar from "@/components/MenuBar";
import SuperUserEmployerSelector from "@/components/common/SuperUserEmployerSelector";
import { isSuperUser } from "@/types/user";
import {
  FileText,
  Send,
  Eye,
  Calendar,
  CheckCircle,
  XCircle,
  Loader2,
  AlertTriangle,
  Info,
} from "lucide-react";
import {
  EpsNoPaymentRequest,
  EpsPreviewRequest,
  EpsValidationRequest,
  EpsFilingResponse,
  EpsPeriodsResponse,
  EpsFilingsResponse,
  EpsValidationResponse,
  EpsPeriod,
  Filing,
  SubmitFilingRequest,
  SubmitFilingResponse,
} from "@/types/filings";
import { FilingTransmission } from "@/types/filings";

interface EPSManagementState {
  selectedUserId: string;
  selectedEmployerId: number | null;
  selectedTaxYear: string;
  selectedTaxMonth: number | null;
  isTestMode: boolean;
  periods: EpsPeriod[];
  existingFilings: Filing[];
  previewXml: string;
  isLoading: boolean;
  isGenerating: boolean;
  isSubmitting: boolean;
  isPreviewing: boolean;
  validationErrors: string[];
  generatedFilingId: number | null;
  lastSubmissionStatus: string | null;
  generatedFilingOrderId: number | null;
  transmissionId: number | null;
  transmissions: FilingTransmission[];
}

const EPSManagement: React.FC = () => {
  const { user } = useAuth();
  const isSuperUserAccount = isSuperUser(user);

  const [state, setState] = useState<EPSManagementState>({
    selectedUserId: "current",
    selectedEmployerId: null,
    selectedTaxYear: "",
    selectedTaxMonth: null,
    isTestMode: true,
    periods: [],
    existingFilings: [],
    previewXml: "",
    isLoading: false,
    isGenerating: false,
    isSubmitting: false,
    isPreviewing: false,
    validationErrors: [],
    generatedFilingId: null,
    lastSubmissionStatus: null,
    generatedFilingOrderId: null,
    transmissionId: null,
    transmissions: [],
  });

  // Redirect non-superusers
  useEffect(() => {
    if (!isSuperUserAccount) {
      toast({
        title: "Access Denied",
        description: "This page is only accessible to superusers.",
        variant: "destructive",
      });
    }
  }, [isSuperUserAccount]);

  // Load periods when employer is selected
  useEffect(() => {
    if (state.selectedEmployerId) {
      loadEpsPeriods();
      loadExistingFilings();
    }
  }, [state.selectedEmployerId]);

  const handleUserChange = (userId: string) => {
    setState(prev => ({
      ...prev,
      selectedUserId: userId,
      selectedEmployerId: userId === "current" ? null : parseInt(userId),
      periods: [],
      existingFilings: [],
      previewXml: "",
      generatedFilingId: null,
      generatedFilingOrderId: null,
      transmissionId: null,
      transmissions: [],
      validationErrors: [],
    }));
  };

  const loadEpsPeriods = async () => {
    if (!state.selectedEmployerId) return;

    setState(prev => ({ ...prev, isLoading: true }));
    try {
      const response = await ApiService.request<EpsPeriodsResponse>(
        EndpointKeys.GET_EPS_PERIODS,
        {
          queryParams: { employer_id: state.selectedEmployerId },
        }
      );

      if (response.success) {
        setState(prev => ({
          ...prev,
          periods: response.periods,
          selectedTaxYear: response.current_tax_year,
        }));
      } else {
        throw new Error("Failed to load EPS periods");
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to load available EPS periods",
        variant: "destructive",
      });
    } finally {
      setState(prev => ({ ...prev, isLoading: false }));
    }
  };

  const fetchExistingFilings = async (): Promise<Filing[]> => {
    if (!state.selectedEmployerId) return [];
    const response = await ApiService.request<EpsFilingsResponse>(
      EndpointKeys.GET_EPS_FILINGS,
      { queryParams: { employer_id: state.selectedEmployerId } }
    );
    return response.success ? response.filings : [];
  };

  const loadExistingFilings = async () => {
    if (!state.selectedEmployerId) return;
    try {
      const filings = await fetchExistingFilings();
      setState(prev => ({ ...prev, existingFilings: filings }));

      // If an EPS already exists for the selected period, wire it up for submission
      const period = getSelectedPeriod();
      if (period && filings.length > 0) {
        console.log('Looking for existing filing for period:', period.period_end);
        console.log('Available filings:', filings.map(f => ({ id: f.id, end: f.coveragePeriodEnd })));
        
        const existing = filings.find(
          f => f.coveragePeriodEnd === period.period_end
        );
        
        if (existing) {
          const latest = existing.latestVersion;
          const doc = latest.documents?.find(d => d.documentType === "RTI") || latest.documents?.[0];
          console.log('Found existing filing:', { filingId: existing.id, versionId: latest.id, docId: doc?.id });
          
          setState(prev => ({
            ...prev,
            generatedFilingId: doc?.id || null,
            generatedFilingOrderId: latest.id,
          }));
          
          toast({
            title: "Existing EPS Found",
            description: `Found existing EPS for this period (Doc ID: ${doc?.id}). You can submit it now.`,
          });
        } else {
          console.log('No existing filing found for period:', period.period_end);
        }
      }
    } catch (error) {
      console.error("Failed to load existing EPS filings:", error);
    }
  };

  const validateRequest = async (): Promise<boolean> => {
    if (!state.selectedEmployerId || !state.selectedTaxYear || !state.selectedTaxMonth) {
      setState(prev => ({
        ...prev,
        validationErrors: ["Please select employer, tax year, and tax month"],
      }));
      return false;
    }

    try {
      const validationRequest: EpsValidationRequest = {
        employer_id: state.selectedEmployerId,
        tax_year: state.selectedTaxYear,
        tax_month: state.selectedTaxMonth,
      };

      const response = await ApiService.request<EpsValidationResponse>(
        EndpointKeys.VALIDATE_EPS_REQUEST,
        {
          method: "POST",
          body: validationRequest,
        }
      );

      if (response.success && response.valid) {
        setState(prev => ({ ...prev, validationErrors: [] }));
        return true;
      } else {
        setState(prev => ({ ...prev, validationErrors: response.errors }));
        return false;
      }
    } catch (error) {
      setState(prev => ({
        ...prev,
        validationErrors: ["Failed to validate request"],
      }));
      return false;
    }
  };

  const handlePreview = async () => {
    if (!(await validateRequest())) return;

    setState(prev => ({ ...prev, isPreviewing: true, previewXml: "" }));

    try {
      const previewRequest: EpsPreviewRequest = {
        employer_id: state.selectedEmployerId!,
        tax_year: state.selectedTaxYear,
        tax_month: state.selectedTaxMonth!,
        is_test: state.isTestMode,
      };

      const response = await ApiService.request<EpsFilingResponse>(
        EndpointKeys.PREVIEW_EPS,
        {
          method: "POST",
          body: previewRequest,
        }
      );

      if (response.success && response.xml_preview) {
        setState(prev => ({ ...prev, previewXml: response.xml_preview! }));
        toast({
          title: "Preview Generated",
          description: "EPS XML preview generated successfully",
        });
      } else {
        throw new Error(response.errors?.join(", ") || "Failed to generate preview");
      }
    } catch (error) {
      toast({
        title: "Preview Failed",
        description: error instanceof Error ? error.message : "Failed to generate preview",
        variant: "destructive",
      });
    } finally {
      setState(prev => ({ ...prev, isPreviewing: false }));
    }
  };

  const handleGenerate = async () => {
    if (!(await validateRequest())) return;

    setState(prev => ({ ...prev, isGenerating: true, generatedFilingId: null }));

    try {
      const isNewVersion = state.generatedFilingId !== null;
      const generateRequest: EpsNoPaymentRequest = {
        employer_id: state.selectedEmployerId!,
        tax_year: state.selectedTaxYear,
        tax_month: state.selectedTaxMonth!,
        is_test: state.isTestMode,
        force_new_version: isNewVersion,
      };

      const response = await ApiService.request<EpsFilingResponse>(
        EndpointKeys.GENERATE_EPS_NO_PAYMENT,
        {
          method: "POST",
          body: generateRequest,
        }
      );

      if (response.success && response.filing_document_id) {
        setState(prev => ({ 
          ...prev, 
          generatedFilingId: response.filing_document_id!,
          generatedFilingOrderId: response.filing_order_id || null,
          previewXml: response.xml_preview || "",
        }));
        
        const isNewVersion = state.generatedFilingId !== null;
        toast({
          title: isNewVersion ? "New EPS Version Generated" : "EPS Generated",
          description: `EPS filing ${isNewVersion ? 'new version' : ''} generated successfully (ID: ${response.filing_document_id})`,
        });

        // Reload existing filings to show the new one
        await loadExistingFilings();
      } else {
        throw new Error(response.errors?.join(", ") || "Failed to generate EPS");
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Failed to generate EPS";

      // If an EPS already exists for the selected period, find it and enable submission
      if (errorMessage.toLowerCase().includes("already exists")) {
        try {
          const filings = await fetchExistingFilings();
          const period = getSelectedPeriod();
          if (period) {
            const match = filings.find(f => f.coveragePeriodEnd === period.period_end);
            if (match && match.latestVersion && match.latestVersion.documents?.length) {
              const doc = match.latestVersion.documents.find(d => d.documentType === "RTI") || match.latestVersion.documents[0];
              setState(prev => ({
                ...prev,
                existingFilings: filings,
                generatedFilingId: doc?.id || null,
                generatedFilingOrderId: match.latestVersion.id,
              }));
              toast({
                title: "Using Existing EPS",
                description: `An EPS already exists for this period. Click "Generate New Version" to create a new version, or submit the existing one (Doc ID: ${doc?.id}).`,
                variant: "default",
              });
              return;
            }
          }
        } catch (_) {
          // fall through to generic error toast
        }
      }

      toast({
        title: "Generation Failed",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setState(prev => ({ ...prev, isGenerating: false }));
    }
  };

  const handleSubmit = async () => {
    if (!state.generatedFilingId) {
      toast({
        title: "No Filing to Submit",
        description: "Please generate an EPS filing first",
        variant: "destructive",
      });
      return;
    }

    setState(prev => ({ ...prev, isSubmitting: true, lastSubmissionStatus: null }));

    try {
      const submitRequest: SubmitFilingRequest = {
        filing_document_id: state.generatedFilingId,
        is_test: state.isTestMode,
      };

      const response = await ApiService.request<SubmitFilingResponse>(
        EndpointKeys.SUBMIT_FILING_MANUALLY,
        {
          method: "POST",
          body: submitRequest,
        }
      );

      if (response.success) {
        setState(prev => ({ 
          ...prev, 
          lastSubmissionStatus: `Submitted successfully. Correlation ID: ${response.correlation_id}`,
          transmissionId: response.transmission_id || null,
        }));
        
        toast({
          title: "Submission Successful",
          description: `EPS submitted to HMRC. Correlation ID: ${response.correlation_id}`,
        });
      } else {
        throw new Error(response.error || "Failed to submit EPS");
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Failed to submit EPS";
      setState(prev => ({ ...prev, lastSubmissionStatus: `Failed: ${errorMessage}` }));
      
      toast({
        title: "Submission Failed",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setState(prev => ({ ...prev, isSubmitting: false }));
    }
  };

  const refreshTransmissions = async () => {
    try {
      await loadExistingFilings();
      if (state.generatedFilingOrderId) {
        const transmissionsResp = await ApiService.request<{ success: boolean; transmissions: FilingTransmission[] }>(
          EndpointKeys.GET_FILING_TRANSMISSIONS,
          {
            method: "GET",
            urlParams: { id: state.generatedFilingOrderId },
          }
        );
        if (transmissionsResp.success) {
          const list = transmissionsResp.transmissions || [];
          setState(prev => ({ ...prev, transmissions: list }));
          if (list.length > 0) {
            const latest = [...list].sort((a, b) => (a.submittedAt < b.submittedAt ? 1 : -1))[0];
            const statusLabel = latest.status || "UNKNOWN";
            const mode = latest.isTest ? "TIL" : "Live";
            setState(prev => ({ ...prev, lastSubmissionStatus: `Latest ${mode} status: ${statusLabel}` }));
          }
        }
      }
    } catch (err) {
      toast({ title: "Refresh Failed", description: "Could not refresh transmissions.", variant: "destructive" });
    }
  };

  const getSelectedPeriod = (): EpsPeriod | null => {
    return state.periods.find(
      p => p.tax_year === state.selectedTaxYear && p.tax_month === state.selectedTaxMonth
    ) || null;
  };

  const selectedPeriod = getSelectedPeriod();

  if (!isSuperUserAccount) {
    return (
      <div className="flex min-h-screen bg-background">
        <div className="flex-1 flex items-center justify-center">
          <Alert className="max-w-md">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              Access denied. This page is only accessible to superusers.
            </AlertDescription>
          </Alert>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-background">
      <div className="hidden md:block fixed md:relative w-64 h-full bg-gray-900 z-20">
        <MenuBar />
      </div>
      
      <div className="flex-1 md:ml-0 overflow-hidden mt-16 md:mt-0">
        <div className="p-4 md:p-6 max-w-7xl space-y-6">
          {/* Header */}
          <Card>
            <CardHeader>
              <div className="flex flex-col space-y-4">
                <div className="flex items-center justify-between">
                  <h1 className="text-3xl font-bold">EPS Management</h1>
                  <Badge variant="outline" className="bg-blue-50 text-blue-700">
                    Superuser Only
                  </Badge>
                </div>
                
                <p className="text-muted-foreground">
                  Generate and submit HMRC Employer Payment Summary (EPS) "No Payment" filings.
                </p>

                {/* Employer Selector */}
                <div className="space-y-2">
                  <SuperUserEmployerSelector
                    selectedUserId={state.selectedUserId}
                    onUserChange={handleUserChange}
                  />
                  {state.selectedUserId === "current" && (
                    <p className="text-sm text-amber-600">
                      <Info className="h-4 w-4 inline mr-1" />
                      Please select an employer to proceed
                    </p>
                  )}
                </div>
              </div>
            </CardHeader>
          </Card>

          {/* Main Content */}
          {state.selectedEmployerId ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Left Column - Form */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <FileText className="h-5 w-5 mr-2" />
                    Generate EPS Filing
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Tax Year Selection */}
                  <div className="space-y-2">
                    <Label htmlFor="tax-year">Tax Year</Label>
                    <Select
                      value={state.selectedTaxYear}
                      onValueChange={(value) => setState(prev => ({ 
                        ...prev, 
                        selectedTaxYear: value,
                        selectedTaxMonth: null 
                      }))}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select tax year" />
                      </SelectTrigger>
                      <SelectContent>
                        {Array.from(new Set(state.periods.map(p => p.tax_year))).map(year => (
                          <SelectItem key={year} value={year}>
                            {year}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Tax Month Selection */}
                  <div className="space-y-2">
                    <Label htmlFor="tax-month">Tax Month</Label>
                    <Select
                      value={state.selectedTaxMonth?.toString() || ""}
                      onValueChange={(value) => setState(prev => ({ 
                        ...prev, 
                        selectedTaxMonth: parseInt(value) 
                      }))}
                      disabled={!state.selectedTaxYear}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select tax month" />
                      </SelectTrigger>
                      <SelectContent>
                        {state.periods
                          .filter(p => p.tax_year === state.selectedTaxYear)
                          .map(period => (
                            <SelectItem key={period.tax_month} value={period.tax_month.toString()}>
                              <div className="flex items-center justify-between w-full">
                                <span>{period.month_name}</span>
                                {period.has_existing_filing && (
                                  <Badge variant="secondary" className="ml-2">
                                    Has Filing
                                  </Badge>
                                )}
                              </div>
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Period Info */}
                  {selectedPeriod && (
                    <Alert>
                      <Calendar className="h-4 w-4" />
                      <AlertDescription>
                        <strong>Period:</strong> {selectedPeriod.period_start} to {selectedPeriod.period_end}
                        {selectedPeriod.has_existing_filing && (
                          <div className="mt-1 text-amber-600">
                            ⚠️ An EPS filing already exists for this period
                          </div>
                        )}
                      </AlertDescription>
                    </Alert>
                  )}

                  {/* Test Mode Toggle */}
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="test-mode"
                      checked={state.isTestMode}
                      onChange={(e) => setState(prev => ({ 
                        ...prev, 
                        isTestMode: e.target.checked 
                      }))}
                    />
                    <Label htmlFor="test-mode">Test Mode (TIL)</Label>
                  </div>

                  {/* Validation Errors */}
                  {state.validationErrors.length > 0 && (
                    <Alert variant="destructive">
                      <XCircle className="h-4 w-4" />
                      <AlertDescription>
                        <ul className="list-disc pl-4">
                          {state.validationErrors.map((error, index) => (
                            <li key={index}>{error}</li>
                          ))}
                        </ul>
                      </AlertDescription>
                    </Alert>
                  )}

                  {/* Action Buttons */}
                  <div className="flex flex-wrap gap-2">
                    <Button
                      onClick={handlePreview}
                      disabled={state.isPreviewing || state.isLoading}
                      variant="outline"
                    >
                      {state.isPreviewing ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <Eye className="h-4 w-4 mr-2" />
                      )}
                      Preview XML
                    </Button>

                    <Button
                      onClick={handleGenerate}
                      disabled={state.isGenerating || state.isLoading}
                    >
                      {state.isGenerating ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <FileText className="h-4 w-4 mr-2" />
                      )}
                      {state.generatedFilingId ? "Generate New Version" : "Generate & Save"}
                    </Button>

                    {state.generatedFilingId && (
                      <Button
                        onClick={handleSubmit}
                        disabled={state.isSubmitting}
                        variant="default"
                      >
                        {state.isSubmitting ? (
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        ) : (
                          <Send className="h-4 w-4 mr-2" />
                        )}
                        Submit to HMRC
                      </Button>
                    )}

                    {state.generatedFilingOrderId && (
                      <Button onClick={refreshTransmissions} variant="outline">
                        Refresh Status
                      </Button>
                    )}
                  </div>

                  {/* Generation Success */}
                  {state.generatedFilingId && (
                    <Alert className="bg-green-50 border-green-200">
                      <CheckCircle className="h-4 w-4 text-green-600" />
                      <AlertDescription>
                        EPS filing generated successfully (ID: {state.generatedFilingId})
                      </AlertDescription>
                    </Alert>
                  )}

                  {/* Submission Status */}
                  {state.lastSubmissionStatus && (
                    <Alert className={state.lastSubmissionStatus.startsWith("Failed") ? 
                      "border-red-200 bg-red-50" : "border-green-200 bg-green-50"}>
                      {state.lastSubmissionStatus.startsWith("Failed") ? (
                        <XCircle className="h-4 w-4 text-red-600" />
                      ) : (
                        <CheckCircle className="h-4 w-4 text-green-600" />
                      )}
                      <AlertDescription>{state.lastSubmissionStatus}</AlertDescription>
                    </Alert>
                  )}
                </CardContent>
              </Card>

              {/* Right Column - Preview & Status */}
              <div className="space-y-6">
                {/* XML Preview */}
                {state.previewXml && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center">
                        <Eye className="h-5 w-5 mr-2" />
                        XML Preview
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <pre className="bg-gray-50 p-4 rounded text-xs overflow-auto max-h-96 border">
                        {state.previewXml}
                      </pre>
                    </CardContent>
                  </Card>
                )}

                {/* Existing Filings */}
                <Card>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <CardTitle>Recent EPS Filings</CardTitle>
                      <Button variant="ghost" onClick={loadExistingFilings}>Reload</Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {state.existingFilings.length > 0 ? (
                      <div className="space-y-2">
                        {state.existingFilings.slice(0, 5).map((filing) => (
                          <div key={filing.id} className="border p-3 rounded">
                            <div className="flex items-center justify-between">
                              <div>
                                <div className="font-medium">
                                  Filing #{filing.id} (v{filing.latestVersion.version})
                                </div>
                                <div className="text-sm text-muted-foreground">
                                  {filing.coveragePeriodStart} to {filing.coveragePeriodEnd}
                                </div>
                                <div className="text-xs text-muted-foreground">
                                  Docs: {filing.latestVersion.documents?.length || 0}
                                  {filing.latestVersion.documents?.map(d => ` ${d.documentType}(${d.id})`).join(',')}
                                </div>
                              </div>
                              <div className="flex flex-col items-end gap-1">
                                <Badge 
                                  variant={
                                    filing.latestVersion.status === "SUCCESS" ? "default" : 
                                    filing.latestVersion.status === "FAILED" ? "destructive" : 
                                    "secondary"
                                  }
                                >
                                  {filing.latestVersion.status}
                                </Badge>
                                {filing.latestVersion.transmissionStatus && (
                                  <Badge variant="outline" className="text-xs">
                                    {filing.latestVersion.transmissionStatus}
                                  </Badge>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-muted-foreground">No EPS filings found</p>
                    )}
                  </CardContent>
                </Card>

                {/* Transmission History (for generated filing) */}
                {state.generatedFilingOrderId && (
                  <Card>
                    <CardHeader>
                      <CardTitle>Transmission History</CardTitle>
                    </CardHeader>
                    <CardContent>
                      {state.transmissions.length > 0 ? (
                        <div className="space-y-2">
                          {state.transmissions.map((t) => (
                            <div key={t.id} className="border p-3 rounded">
                              <div className="flex items-center justify-between">
                                <div>
                                  <div className="font-medium">{t.isTest ? "TIL" : "Live"} — {t.status}</div>
                                  <div className="text-sm text-muted-foreground">
                                    Submitted: {t.submittedAt}
                                    {t.correlationId ? ` • Corr ID: ${t.correlationId}` : ""}
                                  </div>
                                </div>
                              </div>
                              {t.errorMessage && (
                                <div className="text-sm text-red-600 mt-1">{t.errorMessage}</div>
                              )}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-muted-foreground">No transmissions yet</p>
                      )}
                    </CardContent>
                  </Card>
                )}
              </div>
            </div>
          ) : (
            <Card>
              <CardContent className="p-12 text-center">
                <div className="mb-4 rounded-full bg-blue-100 p-4 mx-auto w-16 h-16 flex items-center justify-center">
                  <Info className="h-8 w-8 text-blue-500" />
                </div>
                <h2 className="mb-2 text-xl font-semibold">Select an Employer</h2>
                <p className="text-muted-foreground max-w-md mx-auto">
                  Please select an employer from the dropdown above to view and manage their EPS filings.
                </p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default EPSManagement;
